import { LoanProgram, AdjustmentType } from '@frontend/pricing-search';
import { formatDate, lookupKeyFromValue } from '@frontend/utils';
import { updateBuySideAdjustmentsAction } from '../../data/origin/actionCreators';
import api from '../utils/api';
import Common from './Common';
import {
  convertEPPSAdjustmentsToLNCT,
  createAdjustments,
  eppsToLNCTAdjustmentType,
} from './lockConfirmServices';
import Session, {
  ALL_SUMMARY_PARAMS,
  APPLICATION_NAME,
  BUYSIDE_LOAN_ID,
  IS_DEBUG,
  IS_LO,
  IS_SELLSIDE,
  JWT_TOKEN,
  ORIGIN_SOURCE,
  USER_TYPE,
} from './Session';
import {
  callDebugRestProxy,
  getDebugProxyData,
} from './debugRestProxyServices';
import {
  extractApprovedReason,
  mapTempApprovedReasonToCorrectAbbreviation,
} from '../../view/components/PricingGrid/SlidePriceCards/helpers';
import { RequestActionType } from '../constants/enumTypes';

interface BuysideAdjustment {
  description: string;
  adjustmentType: string;
  rate?: number;
  price?: number;
  priceAdjustmentType?: string;
  priceAdjustmentTypeIndex?: number;
  status?: string;
  approvalDate?: string;
  approvedBy?: string;
  approvedReason?: string;
  uuid?: string;
  id?: string;
  rateLockAdjustmentType?: string;
}

let hasMappedInitial = false;

export const rateSelector = async ({
  loanData,
  loanPrograms,
  isScenarioFlow = false,
  selectedMIAmount,
  pricingScorePrograms,
}: {
  loanData?: any;
  loanPrograms?: LoanProgram[];
  isScenarioFlow?: boolean;
  selectedMIAmount?: number | null;
  pricingScorePrograms?: LoanProgram[];
} = {}) => {
  const {
    requestLockStatus,
    requestAction,
    loanInformation,
    relockRequest,
    eppsUserName,
  } = loanData || {};
  const { loanId: loanIdLoan, lienPosition } = loanInformation || {};
  const {
    specificRateDataID: rateDataIdLoan,
    specificProgramID: programIdLoan,
    effectiveDate: effectiveDateLoan,
  } = relockRequest || {};
  const isLOCmode = Session.get(IS_LO);
  const selectedProductData = Session.get(ALL_SUMMARY_PARAMS);
  const {
    loanId,
    rateDataId,
    programId,
    effectiveDate: selectedEffectiveDate,
    lockEffectiveDate,
    wcpPolicyApplied,
  } = selectedProductData || {};
  const applicationName = isLOCmode
    ? Session.get(APPLICATION_NAME) || 'ENCW'
    : 'EPPS';

  let loanEffectiveDate = relockRequest?.effectiveDate ?? null;
  let loanUseCurrentDateAsEffectiveDate = false;
  const rateDataID = rateDataId || rateDataIdLoan;
  const matchingRateData = (loanPrograms ?? [])
    .map((program) => program.rateDetails)
    .flat(1)
    .find((rateDetail) => rateDetail.rateDataID === rateDataID);
  const selectedProgramId = programId || programIdLoan;
  const pricingCreditScore = (pricingScorePrograms ?? loanPrograms ?? []).find(
    (program) =>
      (!selectedProgramId ||
        String(program.programID) === String(selectedProgramId)) &&
      program.rateDetails?.some(
        (rateDetail) => rateDetail.rateDataID === rateDataID,
      ),
  )?.pricingCreditScore;
  const updateLockDate = matchingRateData?.updateLockDate;
  // EPPS-106193: a WCP policy can resolve a lock period for the selected rate that differs from
  // the one the loan was priced with; pass it back so the lock is placed for that many days
  const wcpLockDays = matchingRateData?.wcpLockDays;
  if (wcpPolicyApplied) {
    loanEffectiveDate = matchingRateData?.rateDate;
    loanUseCurrentDateAsEffectiveDate =
      !lockEffectiveDate && !loanEffectiveDate;
  } else {
    loanEffectiveDate =
      selectedEffectiveDate || loanEffectiveDate || lockEffectiveDate || null;
  }
  const body = {
    loanId: loanId || loanIdLoan,
    sourceApplicationName: applicationName,
    sourceApplicationFormName: Session.get(ORIGIN_SOURCE) || 'lockrequest',
    requestLockStatus,
    requestAction,
    eppsUserName,
    programId: selectedProgramId,
    rateDataID,
    lienPosition: lienPosition || 1,
    effectiveDate: loanEffectiveDate,
    useCurrentDateAsEffectiveDate: loanUseCurrentDateAsEffectiveDate,
    updateLockDate,
    ...(wcpLockDays != null && { wcpLockDays }),
    ...(selectedMIAmount != null && { miAmount: selectedMIAmount }),
    ...(pricingCreditScore != null && {
      overRideCreditScore: pricingCreditScore,
    }),
  };
  if (isScenarioFlow) {
    body.requestAction = 1;
    body.loanChannel = loanInformation.loanChannel;
  }

  let details = {};
  const user = Session.get(USER_TYPE);
  if (Session.get(IS_DEBUG)) {
    return callDebugRestProxy({
      key: 'lockrates',
      pathParameters: { loanId: body.loanId },
      payload: body,
    });
  }
  if (user && user.userType === 'StandAlone') {
    details = await api.selectProgramRate(
      {
        body: JSON.stringify(body),
        customToken: { value: Session.get(JWT_TOKEN) },
        tokenType: 'Bearer',
        loanId,
      },
      {},
    );

    return details;
  }

  // eslint-disable-next-line prefer-const
  let eppsDetails = await api.getWebhookData(
    {
      headers: { 'X-Elli-PAT': await Common.getPATToken() },
      body: JSON.stringify(body),
    },
    await Common.getOriginId(),
    'LockRates',
  );
  // only map adjustments on initial load or when navigating from buyside to sellside and vice versa.

  return eppsDetails;
};

export const validateRateDocInstance = async (rateDocInstanceID) => {
  let result;
  if (Session.get(IS_LO)) {
    const patToken = await Common.getPATToken();
    const originId = await Common.getOriginId();
    result = await api.getRateDocInstanceV1(
      { patToken, originId },
      rateDocInstanceID,
    );
  } else {
    const jwtToken = await Session.get(JWT_TOKEN);
    result = await api.getRateDocInstanceV2({ jwtToken }, rateDocInstanceID);
  }
  const jsonData = await result.json();
  return result.ok && jsonData.status === 1;
};

export const getRateSheetDocument = async (rateDocInstanceID) => {
  let result;
  if (Session.get(IS_LO)) {
    const patToken = await Common.getPATToken();
    const originId = await Common.getOriginId();
    result = await api.getRateSheetDocumentV1(
      { patToken, originId },
      rateDocInstanceID,
    );
  } else {
    const jwtToken = await Session.get(JWT_TOKEN);
    result = await api.getRateSheetDocumentV2({ jwtToken }, rateDocInstanceID);
  }
  if (result.ok) {
    const blob = await result.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', 'ratesheet');
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild?.(link);
  }
  return result;
};

export const getEncompassSettings = async () => {
  let result: any | null = null;
  if (Session.get(IS_DEBUG)) {
    result = await getDebugProxyData('encompasssettings');
  } else if (Session.get(IS_LO)) {
    result = await api.getEncompassSettingsV1();
  }
  return result;
};
